/**
 * Flightline — AI shared math helpers
 * --------------------------------------------------------------------------
 * Location : /src/domain/ai/aiMath.ts
 * Runs in  : Simulation Web Worker (pure functions, no UI or DOM concerns).
 *
 * Small numeric helpers plus a deterministic random number generator. The AI
 * must be reproducible: the same world state and the same seeds always give
 * the same competitor decisions, so nothing here touches `Math.random`.
 */

import type { AirportId, RouteKey } from '../models';

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

export function round(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** `numerator / denominator`, or `fallback` when the quotient is not finite. */
export function safeDivide(numerator: number, denominator: number, fallback: number = 0): number {
  const quotient = numerator / denominator;
  return Number.isFinite(quotient) ? quotient : fallback;
}

/** Directional route key, "ORG-DST". Matches the keys of `DemandMatrix.entries`. */
export function routeKeyOf(originId: AirportId, destinationId: AirportId): RouteKey {
  return `${originId}-${destinationId}`;
}

/** Order-independent key for an airport pair, used to merge both directions of a market. */
export function pairKeyOf(a: AirportId, b: AirportId): string {
  return a <= b ? `${a}~${b}` : `${b}~${a}`;
}

/** FNV-1a 32-bit string hash. Stable across platforms. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Mulberry32 pseudo-random generator; returns a function yielding floats in [0, 1). */
export function createRng(seed: number): () => number {
  let state = seed >>> 0;
  return (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Picks one index with probability proportional to `weights`; -1 when every weight is zero. */
export function pickWeightedIndex(weights: readonly number[], random: () => number): number {
  let total = 0;
  for (const weight of weights) total += Math.max(0, weight);
  if (total <= 0) return -1;
  let cursor = random() * total;
  for (let index = 0; index < weights.length; index += 1) {
    cursor -= Math.max(0, weights[index] ?? 0);
    if (cursor < 0) return index;
  }
  return weights.length - 1;
}

export function pad(value: number, width: number): string {
  return String(Math.trunc(value)).padStart(width, '0');
}
